<template>
  <Nav-bar :has_back="true" :has_right="false" title="订单金额明细"></Nav-bar>
  <div class="order-details-container">
    <!-- 总金额卡片 -->
    <div class="total-amount-card">
      <div class="amount">
        {{ parseFloat(state.amount).toFixed(2) }}<span class="unit">元</span>
      </div>
      <div class="amount-desc">电费 + 服务费 + 超时占位费</div>
    </div>

    <!-- 电费明细 -->
    <div class="detail-section" v-if="state.billingDetail.length > 0">
      <div class="section-header">
        <div class="section-title">电费</div>
      </div>
      <div class="detail-items">
        <div
          class="detail-item"
          v-for="(item, index) in state.billingDetail"
          :key="index"
        >
          <span class="time-range">{{ formatPeriod(item.period) }}</span>
          <span class="rate"
            >{{ getBillingRateText(item) }} X
            {{ parseFloat(item.power_consumption).toFixed(4) }}度</span
          >
          <span class="amount"
            >{{ parseFloat(item.charging_amount).toFixed(2) }}元</span
          >
        </div>
      </div>
    </div>

    <!-- 服务费明细 -->
    <div class="detail-section" v-if="state.billingDetail.length > 0">
      <div class="section-header">
        <div class="section-title">服务费</div>
      </div>
      <div class="detail-items">
        <div
          class="detail-item"
          v-for="(item, index) in state.billingDetail"
          :key="index"
        >
          <span class="time-range">{{ formatPeriod(item.period) }}</span>
          <span class="rate"
            >{{ getServiceRateText(item) }} X
            {{ parseFloat(item.power_consumption).toFixed(4) }}度</span
          >
          <span class="amount"
            >{{ parseFloat(item.service_amount).toFixed(2) }}元</span
          >
        </div>
      </div>
    </div>

    <!-- 超时占位费明细 -->
    <div
      class="detail-section"
      v-if="state.occupancyDetail && parseFloat(state.occupancyAmount) > 0"
    >
      <div class="section-header">
        <div class="section-title">超时占位费</div>
    
      </div>
      <div class="detail-items">
        <div class="detail-item" v-if="state.occupancyDetail.period">
          <span class="time-range"
            >{{ formatOccupancyTime(state.occupancyDetail.period.from) }}-{{
              formatOccupancyTime(state.occupancyDetail.period.to)
            }}</span
          >
        </div>
        <div class="detail-item" >
          <span class="time-range"
            >占位时长：{{
              formatOccupancyDuration(state.occupancyDetail.period)
            }}</span
          >
        </div>
        <div class="detail-item" v-if="state.occupancyDetail.unit_price">
          <span class="time-range"
            >收费规则：{{ state.occupancyDetail.unit_price }}元/分钟</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import { reactive, onMounted } from "vue";
import { useRoute } from "vue-router";
import request from "@/api/request";

export default {
  name: "OrderBillingDetails",
  components: {
    NavBar: NavBar,
  },
  setup() {
    const route = useRoute();
    const state = reactive({
      orderId: route.params.id,
      amount: "0",
      totalChargingAmount: "0",
      totalServiceAmount: "0",
      occupancyAmount: "0",
      billingDetail: [],
      billingRule: [],
      occupancyDetail: null,
      loading: false,
    });

    // 获取订单详情
    function getOrderDetails() {
      if (!state.orderId) return;

      state.loading = true;

      request
        .getOrdersDetails(state.orderId)
        .then((res) => {
          state.loading = false;

          // 设置基本金额信息
          state.amount = res.data.amount || "0";
          state.totalChargingAmount = res.data.charge_amount || "0";
          state.totalServiceAmount = res.data.total_service_amount || "0";
          state.occupancyAmount = res.data.occupancy_detail.amount || "0";

          // 设置计费详情
          state.billingDetail = res.data.billing_detail || [];
          state.billingRule = res.data.billing_rule || [];
          state.occupancyDetail = res.data.occupancy_detail || null;
        })
        .catch((error) => {
          state.loading = false;
          console.error("获取订单详情失败:", error);
        });
    }

    // 格式化时间段
    function formatPeriod(period) {
      if (!period) return "";
      const parts = period.split("..");
      if (parts.length !== 2) return period;

      const start = parseInt(parts[0]);
      const end = parseInt(parts[1]);

      const startHour = Math.floor(start / 3600);
      const startMin = Math.floor((start % 3600) / 60);
      const endHour = Math.floor(end / 3600);
      const endMin = Math.floor((end % 3600) / 60);

      const formatTime = (hour, min) => {
        return `${hour.toString().padStart(2, "0")}:${min
          .toString()
          .padStart(2, "0")}`;
      };

      return `${formatTime(startHour, startMin)}-${formatTime(
        endHour,
        endMin
      )}`;
    }

    // 格式化占位时间
    function formatOccupancyTime(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const hour = date.getHours().toString().padStart(2, "0");
      const minute = date.getMinutes().toString().padStart(2, "0");
      const second = date.getSeconds().toString().padStart(2, "0");
      return `${month}/${day} ${hour}:${minute}:${second}`;
    }

    // 格式化占位时长
    function formatOccupancyDuration(timeObj) {
      if (!timeObj) return "";
      
      // 如果传入的是数字（分钟），保持原有逻辑
      if (typeof timeObj === 'number') {
        const hours = Math.floor(timeObj / 60);
        const mins = timeObj % 60;
        let result = "";
        if (hours > 0) result += `${hours}小时`;
        if (mins > 0) result += `${mins}分钟`;
        return result || "0分钟";
      }
      
      // 如果传入的是时间对象，计算时间差
      if (timeObj.from && timeObj.to) {
        const fromTime = new Date(timeObj.from);
        const toTime = new Date(timeObj.to);
        const diffMs = toTime - fromTime;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        
        const hours = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;
        let result = "";
        if (hours > 0) result += `${hours}小时`;
        if (mins > 0) result += `${mins}分钟`;
        return result || "0分钟";
      }
      
      return "";
    }

    // 根据时间范围匹配计费规则
    function findRuleByTimeRange(period) {
      if (!period || state.billingRule.length === 0) {
        return state.billingRule[0] || null;
      }

      const parts = period.split("..");
      if (parts.length !== 2) {
        return state.billingRule[0] || null;
      }

      const startSeconds = parseInt(parts[0]);
      const endSeconds = parseInt(parts[1]);

      // 转换为时间格式
      const startHour = Math.floor(startSeconds / 3600);
      const startMin = Math.floor((startSeconds % 3600) / 60);
      const endHour = Math.floor(endSeconds / 3600);
      const endMin = Math.floor((endSeconds % 3600) / 60);

      const formatTime = (hour, min) => {
        return `${hour.toString().padStart(2, "0")}:${min
          .toString()
          .padStart(2, "0")}`;
      };

      const periodStart = formatTime(startHour, startMin);
      const periodEnd = formatTime(endHour, endMin);

      // 查找匹配的规则
      const matchedRule = state.billingRule.find((rule) => {
        return rule.from <= periodStart && rule.to >= periodEnd;
      });

      return matchedRule || state.billingRule[0];
    }

    // 获取电费费率文本
    function getBillingRateText(billingItem) {
      const rule = findRuleByTimeRange(billingItem.period);
      return rule
        ? `${parseFloat(rule.power_rate).toFixed(2)}元/度`
        : "费率未知";
    }

    // 获取服务费费率文本
    function getServiceRateText(billingItem) {
      const rule = findRuleByTimeRange(billingItem.period);
      return rule
        ? `${parseFloat(rule.service_rate).toFixed(2)}元/度`
        : "费率未知";
    }

    onMounted(() => {
      getOrderDetails();
    });

    return {
      state,
      formatPeriod,
      formatOccupancyTime,
      formatOccupancyDuration,
      getBillingRateText,
      getServiceRateText,
    };
  },
};
</script>

<style lang="scss" scoped>
.order-details-container {
  padding: 16px;
  padding-top: 60px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.total-amount-card {
  background: white;
  border-radius: 12px;
  padding: 32px 16px;
  margin-bottom: 16px;
  text-align: center;

  .amount {
    font-size: 48px;
    font-weight: bold;
    color: #ff8800;
    margin-bottom: 8px;

    .unit {
      font-size: 24px;
      margin-left: 4px;
    }
  }

  .amount-desc {
    font-size: 14px;
    color: #ff8800;
    font-weight: 500;
  }
}

.detail-section {
  background: white;
  border-radius: 12px;
  padding: 20px 16px;
  margin-bottom: 16px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 18px;
    font-weight: bold;
    color: #333;
  }

  .section-amount {
    font-size: 24px;
    font-weight: bold;
    color: #333;
  }

  .detail-items {
    .detail-item {
      justify-content: space-between;
      align-items: flex-start;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      .time-range {
        font-size: 14px;
        color: #333;
        font-weight: 500;
        flex: 0 0 80px;
        margin-right: 8px;
      }

      .rate {
        font-size: 14px;
        color: #666;
        flex: 1;
        text-align: left;
        word-break: break-word;
        overflow-wrap: break-word;
        line-height: 1.4;
        margin-right: 8px;
      }

      .amount {
        font-size: 14px;
        color: #333;
        font-weight: 500;
        text-align: right;
        flex: 0 0 60px;
      }
    }
  }
}

/* 超时占位费特殊样式 */
.detail-section:last-child {
  .detail-items {
    .detail-item {
      justify-content: flex-start;

      .time-range {
        flex: none;
        width: 100%;
      }
    }
  }
}
</style>